import React, { useContext, useState } from 'react';
import { NavItem } from 'reactstrap';
import { NavLink } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Button } from 'react-bootstrap';
import { customerSignInRequest } from '../../authConfig';
import { SidebarContext } from './SidebarContext';
import TopBar, { TopBarSidebar } from '../Common/Navigation/TopBar';
import ConstellationLogo from '../ConstellationLogo';
import BusinessSwitcher from './BusinessSwitcher';
import DevToolsMenu from './DevToolsMenu';
import ProfileMenu from './ProfileMenu';
import ProfileSiteAccess from './ProfileSiteAccess';
import { UserTemplate } from '../UserAccess/UserTemplate';
import { KaEmployeeTemplate } from '../UserAccess/KaEmployeeTemplate';
import { UserBusiness, UserProfile } from '../UserAccess/UserContext';
import { useUser } from '../UserAccess/useUser';
import { useKaEmployee } from '../UserAccess/useKaEmployee';
import { useSelectedBusiness } from '../UserAccess/useSelectedBusiness';
import { usePermission } from '../UserAccess/usePermission';
import Confirm from '../Common/Confirm';

export const NavButton = (props: { text: string, onClick: () => void }) => {
  return (
    <NavItem>
      <Button
        className="btn-ghost-primary"
        variant="link"
        size="sm"
        onClick={props.onClick}>
        <strong>{props.text}</strong>
      </Button>
    </NavItem>
  );
}

export function NavMenu() {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { isCollapsed } = useContext(SidebarContext);
  const customerSignIn = () => instance.loginRedirect(customerSignInRequest);

  const sidebar: TopBarSidebar = !isAuthenticated ? 'none' : isCollapsed ? 'collapsed' : 'expanded';

  return (
    <TopBar
      sidebar={sidebar}
      brand={<ConstellationLogo />}>
      {!isAuthenticated && <>
        <NavButton text="Sign In" onClick={customerSignIn} />
      </>}
      <KaEmployeeTemplate>
        <EmployeeSwitcher />
        <MenuDivider />
        <EmployeeProfileMenu />
      </KaEmployeeTemplate>
      <UserTemplate>
        <UserSwitcher />
        <MenuDivider />
        <DevToolsMenu />
        <UserProfileMenu />
      </UserTemplate>
    </TopBar>
  );
}

const MenuDivider = () =>
  <li className="nav-item d-flex align-items-center" aria-hidden="true">
    <span className="vr" style={{ height: '1.5rem', backgroundColor: '#e7eaf3', opacity: 1 }}></span>
  </li>;

const SettingsLinks = () => {
  const { administrator } = useSelectedBusiness();
  const canModifySettings = usePermission('settingsModify');
  if (!canModifySettings) return null;

  return <>
    <div className="dropdown-divider"></div>
    <span className="dropdown-header">Settings</span>
    {administrator && <>
      <NavLink className="btn-ghost-secondary dropdown-item" to="/users"><strong>Users</strong></NavLink>
      <NavLink className="btn-ghost-secondary dropdown-item" to="/sites"><strong>Sites</strong></NavLink>
    </>}
    <NavLink className="btn-ghost-secondary dropdown-item" to="/datasharing"><strong>Data Sharing</strong></NavLink>
    <NavLink className="btn-ghost-secondary dropdown-item" to="/ordersettings"><strong>Orders</strong></NavLink>
    <NavLink className="btn-ghost-secondary dropdown-item" to="/purchaseordersettings"><strong>Purchase Orders</strong></NavLink>
    <NavLink className="btn-ghost-secondary dropdown-item" to="/emailsettings"><strong>Emails</strong></NavLink>
  </>;
}

const UserSwitcher = () => {
  const user = useUser();
  const [businessToSelect, setBusinessToSelect] = useState<UserBusiness | undefined>(undefined);

  const businesses = [...user.businesses]
    .sort((a, b) => prioritizeNonSandboxBusinesses(a, b) || alphabeticallyByName(a, b));

  const changeSelectedBusiness = (businessId: string) => {
    const business = findUserBusinessById(user, businessId)!;

    if (business.sandbox && !user.selectedBusiness?.sandbox) {
      setBusinessToSelect(business);
    } else user.setSelectedBusiness(business);
  }

  const confirmSandboxBusiness = (business: UserBusiness) => {
    user.setSelectedBusiness(business);
    setBusinessToSelect(undefined);
  }

  return <>
    <Confirm
      visible={!!businessToSelect}
      title='Switch to Sandbox Mode'
      body={`Do you want to leave the production version of Constellation to enter the sandbox?
              Data entered here will not go to production sites.`}
      isLoading={false}
      onDismiss={() => setBusinessToSelect(undefined)}
      onConfirm={() => { confirmSandboxBusiness(businessToSelect!) }} />
    <BusinessSwitcher
      selected={user.selectedBusiness}
      businesses={businesses}
      onSelect={changeSelectedBusiness}>
      {user.selectedBusiness && <SettingsLinks />}
    </BusinessSwitcher>
  </>;
}

const UserProfileMenu = () => {
  const user = useUser();
  const role = user.selectedBusiness?.userRole;

  return (
    <ProfileMenu
      email={user.email}
      onSignOut={user.signOut}
      badges={role && <span className="badge bg-soft-primary text-primary">{role.name}</span>}>
      {user.isDevelopment && user.selectedBusiness && <ProfileSiteAccess />}
    </ProfileMenu>
  );
}

const EmployeeSwitcher = () => {
  const employee = useKaEmployee();
  const businesses = [...employee.businesses].sort(alphabeticallyByName);

  return (
    <BusinessSwitcher
      selected={employee.selectedBusiness}
      businesses={businesses}
      onSelect={businessId => employee.setSelectedBusiness(businesses.find(isBusinessForId(businessId))!)} />
  );
}

const EmployeeProfileMenu = () => {
  const employee = useKaEmployee();

  return (
    <ProfileMenu
      email={employee.email}
      onSignOut={employee.signOut}
      badges={<span className="badge bg-soft-info text-info">Kahler Employee</span>} />
  );
}

const prioritizeNonSandboxBusinesses = (a: UserBusiness, b: UserBusiness) => Number(a.sandbox) - Number(b.sandbox);

const alphabeticallyByName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);

const findUserBusinessById = (userProfile: UserProfile, businessId: string) =>
  userProfile.businesses.find(isBusinessForId(businessId)) ?? null;

const isBusinessForId = (id: string | undefined) =>
  (business: { id: string }) => business.id.toLowerCase() === id?.toLowerCase();
